import { S3 } from '@aws-sdk/client-s3';

import {
  HomePageDataQuery,
  type Playlist,
  type Ribbon,
  type SpotlightUnit,
  type Video
} from 'api';

export interface TrendingData {
  trendingTalks: Array<{ talkId: string; rank: number }>;
}

export async function fetchTrendingDataFromS3(): Promise<TrendingData> {
  const Bucket = 'ted-conferences-analytics';

  if (
    !process.env.AWS_REGION ||
    !process.env.AWS_ACCESS_ID ||
    !process.env.AWS_SECRET_KEY
  ) {
    throw new Error('Missing required AWS environment variables');
  }

  const s3 = new S3({
    region: process.env.AWS_REGION,
    credentials: {
      accessKeyId: process.env.AWS_ACCESS_ID,
      secretAccessKey: process.env.AWS_SECRET_KEY
    }
  });

  const s3objects = await s3.listObjects({
    Bucket,
    Prefix: 'roadrunner/',
    Delimiter: '/'
  });

  if (!s3objects.Contents || s3objects.Contents.length === 0) {
    throw new Error('No S3 objects found');
  }

  const s3ObjectResponse = await s3.getObject({
    Bucket,
    Key: s3objects.Contents[s3objects.Contents.length - 1].Key
  });

  if (!s3ObjectResponse.Body) {
    throw new Error('No response body from S3');
  }

  const response = new Response(
    await s3ObjectResponse.Body.transformToByteArray()
  );
  return response.json();
}

export async function fetchAndRankTrendingTalks(
  trendingData: TrendingData,
  data: HomePageDataQuery
): Promise<{
  trendingTalks: Array<unknown>;
}> {
  const trendingRanks: { [key: string]: number } = {};
  const trendingIds: string[] = [];

  trendingData.trendingTalks.forEach(t => {
    trendingIds.push(t.talkId);
    trendingRanks[t.talkId] = t.rank;
  });

  let rankedTalks: Array<unknown> = [];

  if (data && data.trendingTalks) {
    data.trendingTalks.nodes?.forEach(n => {
      if (n && n.id) {
        rankedTalks[trendingRanks[n.id]] = n;
      }
    });
  }

  rankedTalks = rankedTalks.filter(t => t !== null);
  const trendingTalks = data?.trendingTalks?.nodes ? rankedTalks : [];

  return { trendingTalks };
}

export async function fetchPlatformData(data: HomePageDataQuery): Promise<{
  programmerRibbons: Ribbon[];
  spotlightUnits: SpotlightUnit[];
  newestTalks: Video[];
  playlists: Playlist[];
}> {
  // Clone arrays before modifying to avoid mutating original data
  const ribbons = [...(data?.platform?.ribbons?.nodes || [])].filter(
    (ribbon): ribbon is Ribbon => ribbon !== null
  );
  const spotlightUnits = [...(data?.platform?.spotlightUnits?.nodes || [])]
    .filter((unit): unit is SpotlightUnit => unit !== null)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const newestTalks = [...(data?.newestTalks?.nodes || [])].filter(
    (talk): talk is Video => talk !== null
  );
  // Ensure no mutation happens on pancakes.nodes array
  const playlists = [
    ...(data?.platform?.pancakes?.nodes?.[0]?.playlists?.nodes || [])
  ].filter((playlist): playlist is Playlist => playlist !== null);

  const sortFn = (ribbon: Ribbon) => ribbon.order ?? 0;
  const programmerRibbons = ribbons
    .slice() // Clone the ribbons array before sorting
    .sort((a, b) => sortFn(a) - sortFn(b))
    .filter(ribbon => ribbon.live === true);

  return {
    programmerRibbons,
    spotlightUnits,
    newestTalks,
    playlists
  };
}

export function relativeTedUrl(url: string): string {
  const tedUrlPattern = /^https?:\/\/(www\.)?ted\.com/;
  if (tedUrlPattern.test(url)) {
    return url.replace(tedUrlPattern, '');
  }
  // Return the original URL if it's not a TED .com url
  return url;
}
